import { isString } from "lodash";
import { useEffect, useState } from "react";

import type { CSSProperties } from "@mantine/core";
import type { RefObject } from "react";
import type { DropdownItem, DropdownPosition } from "./types";

export const openedInputStyle: Record<DropdownPosition, CSSProperties> = {
  bottom: {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  top: {
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  },
};

export function getPropsFromComboboxItem(value: string | DropdownItem): DropdownItem {
  if (isString(value)) {
    return {
      disabled: false,
      label: value,
      value,
    };
  }

  return value;
}

export function getReactNodeFromItems(items: (string | DropdownItem)[]) {
  return (value?: string | null) => {
    const item = items.find((item) => getPropsFromComboboxItem(item).value === value);
    if (item) {
      const { children, label } = getPropsFromComboboxItem(item);
      return children || label;
    }

    return null;
  };
}

export function isItemInQuery(query: string) {
  const sanitized = query.trim().toLocaleLowerCase();

  return (item: string | DropdownItem) => {
    const { label, value } = getPropsFromComboboxItem(item);
    return (
      value.toLocaleLowerCase().includes(sanitized) ||
      label?.toLocaleLowerCase().includes(sanitized)
    );
  };
}

export function useDropdownPosition(ref: RefObject<HTMLDivElement | null>) {
  const [position, setPosition] = useState<DropdownPosition>();

  useEffect(() => {
    if (ref.current) {
      const observer = new MutationObserver(() => {
        setPosition(ref.current?.dataset["position"] as DropdownPosition);
      });
      observer.observe(ref.current, { attributes: true, attributeFilter: ["data-position"] });
      // Initial position
      setPosition(ref.current.dataset["position"] as DropdownPosition);

      return () => observer.disconnect();
    }
  }, [ref.current]);

  return position;
}
